import React from 'react';
import { Table } from '@unisporkal/alliance-ui';
import { useTranslations } from '@unisporkal/localization';
import assetSizeLabel from '../../../../support/assetSizeLabel';
import type { IncludedSize } from '../types';
import styles from './IncludedSizesTable.module.scss';

type IncludedSizesTableProps = {
  sizes: IncludedSize[];
  ariaLabel: string;
  isVideo?: boolean;
};

const formatPixels = (width?: number, height?: number) =>
  width !== undefined && height !== undefined ? `${width} x ${height} px` : '';

const formatLocale = (
  width: string,
  height: string,
  unit: 'inches' | 'centimeters'
) => `${width} x ${height} ${unit === 'inches' ? 'in' : 'cm'}`;

const ImageRow = ({
  size,
  t,
}: {
  size: IncludedSize;
  t: (key: string, params?: Record<string, unknown>) => string;
}) => (
  <>
    <Table.Cell className={styles.labelCell}>
      {assetSizeLabel(t, size)}
    </Table.Cell>
    <Table.Cell className={styles.dimsCell}>
      <div>{formatPixels(size.pixelWidth, size.pixelHeight)}</div>
      <div className={styles.locale}>
        ({formatLocale(size.localeWidth, size.localeHeight, size.unit)})
      </div>
    </Table.Cell>
    <Table.Cell className={styles.dpiCell}>
      {size.dpi !== undefined ? `${size.dpi} dpi` : ''}
    </Table.Cell>
    <Table.Cell className={styles.mpCell}>{`${size.megapixels} MP`}</Table.Cell>
  </>
);

const VideoRow = ({ size }: { size: IncludedSize }) => (
  <>
    <Table.Cell className={styles.labelCell}>
      <div>{size.definition}</div>
      {size.codec && <div className={styles.codec}>{size.codec}</div>}
    </Table.Cell>
    <Table.Cell className={styles.videoDimsCell}>
      {formatPixels(size.pixelWidth, size.pixelHeight)}
    </Table.Cell>
    <Table.Cell className={styles.fpsCell}>
      {size.frameRate !== undefined ? `${size.frameRate} fps` : ''}
    </Table.Cell>
    <Table.Cell className={styles.formatCell}>
      {size.contentType ?? ''}
    </Table.Cell>
  </>
);

const IncludedSizesTable = ({
  sizes,
  ariaLabel,
  isVideo,
}: IncludedSizesTableProps) => {
  const t = useTranslations();
  return (
    <Table
      aria-label={ariaLabel}
      className={styles.sizesTable}
      data-testid="included-sizes-table"
    >
      <Table.Body>
        {sizes.map((size) => (
          <Table.Row
            key={size.name}
            data-testid={`included-sizes-row-${size.name}`}
          >
            {isVideo ? (
              <VideoRow size={size} />
            ) : (
              <ImageRow
                size={size}
                t={t}
              />
            )}
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  );
};

export default IncludedSizesTable;
